import React, { useEffect, useMemo, useState } from 'react';
import { Input, Select } from 'antd';
import dayjs from 'dayjs';

import { InputDate, optionSearchFilter } from '../Input';
import { parseDateInput } from '../../utils/dateParse';
import {
	DATE_PRESETS,
	PRESET_BETWEEN,
	PRESET_SPECIFIC,
	PRESET_TYPED,
	betweenPart,
	datePresetOptions,
	isModePreset,
	presetParts,
	presetPoint,
} from '../../utils/datePresets';
import { isSpanRow } from '../../utils/kqlBuilder';
import { NO_TEXT_ASSIST } from '../../utils/fieldAssist';

/**
 * The value control for a date row: one menu that covers every way people ask
 * a date question.
 *
 * Periods (Today, This week, Overdue, Next 7 days…) commit TWO conditions and
 * turn the row into a span row — the operator select steps aside, because
 * "This week" is not a comparison and pretending it is was the old builder's
 * whole problem. Between… opens two calendars. Specific date… is the calendar
 * that was already here. Type a date… is the field's own grammar
 * (`utils/dateParse.js`) in the filter, so "fri", "+7d" and "9/14" mean in a
 * filter exactly what they mean in the form — one grammar, not three.
 *
 * The control is derived from the row on every render (a query typed in the
 * Advanced tab, or a link somebody was sent, shows the preset it matches), and
 * the only local state is which door is OPEN — the half-typed text and the
 * calendar you have not picked from yet.
 */

const modeFromRow = (row) => {
	if (isSpanRow(row)) return row.preset || PRESET_BETWEEN;
	if (row?.value?.type === 'rel') {
		const preset = DATE_PRESETS.find(entry => entry.point === row.value.v);
		return preset ? preset.value : undefined;
	}
	if (row?.value?.type === 'date') return PRESET_SPECIFIC;
	return undefined;
};

/**
 * The day a Between… half stands for, as the calendar should show it. The
 * upper bound is stored EXCLUSIVE (see `patchBetween`), so the day the user
 * picked is the day before it.
 */
const partValue = (row, index) => {
	const part = isSpanRow(row) ? row.parts?.[index] : null;
	const stored = part?.value?.v || null;
	if (!stored) return null;
	if (part.cmp !== 'lt') return stored;
	const day = dayjs(stored);
	return day.isValid() ? day.subtract(1, 'day').format('YYYY-MM-DD') : stored;
};

export default function FilterDateValue({ row, field, disabled = false, onPatch }) {
	const derived = modeFromRow(row);
	// Which door is open. The row answers it once a value exists; until then
	// (an empty Between…, a Type a date… with nothing typed) only this knows.
	const [mode, setMode] = useState(derived);
	const [typed, setTyped] = useState('');

	// A row that changes underneath the control — the field was swapped, the
	// Advanced tab was edited — re-derives rather than keeping a stale door.
	useEffect(() => {
		setMode(current => {
			if (!derived || derived === current) return current;
			// …except that a TYPED date is a date literal, exactly like one
			// picked from the calendar. Deriving blindly would swap the box
			// somebody is typing in for a calendar on their first keystroke.
			if (current === PRESET_TYPED && derived === PRESET_SPECIFIC) return current;
			return derived;
		});
	}, [derived]);

	const withTime = field?.dataType === 'date-time';
	const parseMode = withTime ? 'date-time' : 'date';

	const typedReading = useMemo(() => {
		const text = typed.trim();
		if (!text) return null;
		return parseDateInput(text, { mode: parseMode });
	}, [typed, parseMode]);

	const handlePreset = (value) => {
		setMode(value);
		if (isModePreset(value)) {
			// Between… starts as an EMPTY span (two ends, neither picked): an
			// incomplete span contributes nothing to the query, so the list
			// does not lurch to "everything since Sep 1" between the two picks.
			if (value === PRESET_BETWEEN) {
				onPatch({ kind: 'span', preset: PRESET_BETWEEN, parts: [], value: undefined });
				return;
			}
			setTyped('');
			onPatch({ kind: undefined, preset: undefined, parts: undefined, value: undefined });
			return;
		}
		const parts = presetParts(value);
		if (parts) { onPatch({ kind: 'span', preset: value, parts, value: undefined }); return; }
		const point = presetPoint(value);
		if (point) onPatch({ kind: undefined, preset: undefined, parts: undefined, raw: point });
	};

	/**
	 * Both ends are DAYS, and the upper one is stored EXCLUSIVE — `< the day
	 * after` (utils/datePresets `betweenPart`, which a column header's custom
	 * range writes through too).
	 */
	const patchBetween = (index, stored) => {
		const parts = [...(isSpanRow(row) ? row.parts || [] : [])];
		parts[index] = betweenPart(index, stored);
		onPatch({ kind: 'span', preset: PRESET_BETWEEN, parts, value: undefined });
	};

	const inputName = `filter-value-${row.id}`;

	return (
		<div className="list-filter__date">
			<Select
				className="list-filter__value-input"
				placeholder="When"
				disabled={disabled}
				value={mode}
				options={datePresetOptions()}
				showSearch
				filterOption={optionSearchFilter}
				// Sixteen entries in three groups: the whole menu is shorter
				// than the virtual window's own overhead, and a preset nobody
				// can reach without scrolling a popover inside a popover is a
				// preset nobody uses.
				virtual={false}
				listHeight={420}
				onChange={handlePreset}
			/>

			{mode === PRESET_SPECIFIC ? (
				<InputDate
					elementName={inputName}
					elementLabel=""
					showTime={withTime}
					value={row.value?.type === 'date' ? row.value.v : null}
					disabled={disabled}
					onChange={(_name, stored) => {
						if (!stored) { onPatch({ raw: undefined }); return; }
						// The field commits 'YYYY-MM-DD' for a date and an ISO
						// instant for a date-time; the grammar's literals are
						// day- or minute-granular, so a date passes straight
						// through and a date-time loses its seconds.
						if (!withTime) { onPatch({ raw: stored }); return; }
						const parsed = dayjs(stored);
						onPatch({ raw: parsed.isValid() ? parsed.format('YYYY-MM-DDTHH:mm') : undefined });
					}}
				/>
			) : null}

			{mode === PRESET_TYPED ? (
				<>
					<Input
						{...NO_TEXT_ASSIST}
						className="list-filter__value-input"
						aria-label={`${field?.label || 'Date'} — type a date`}
						placeholder="fri · +7d · 9/14"
						disabled={disabled}
						value={typed}
						onChange={event => {
							const text = event.target.value;
							setTyped(text);
							const reading = parseDateInput(text.trim(), { mode: parseMode });
							if (!reading) { onPatch({ raw: undefined }); return; }
							onPatch({
								raw: withTime
									? dayjs(reading.value).format('YYYY-MM-DDTHH:mm')
									: reading.value,
							});
						}}
					/>
					{typed.trim() ? (
						<p className={`list-filter__reading${typedReading ? '' : ' list-filter__reading--bad'}`}>
							{typedReading ? typedReading.confirm : 'Not a date'}
						</p>
					) : null}
				</>
			) : null}

			{mode === PRESET_BETWEEN ? (
				<div className="list-filter__between">
					<InputDate
						elementName={`${inputName}-from`}
						elementLabel=""
						value={partValue(row, 0)}
						disabled={disabled}
						onChange={(_name, stored) => patchBetween(0, stored)}
					/>
					<span className="list-filter__between-dash" aria-hidden="true">–</span>
					<InputDate
						elementName={`${inputName}-to`}
						elementLabel=""
						value={partValue(row, 1)}
						disabled={disabled}
						onChange={(_name, stored) => patchBetween(1, stored)}
					/>
				</div>
			) : null}
		</div>
	);
}
